import { Outlet } from 'react-router';
import { useApolloClient } from '@apollo/client/react';
import { useTranslation } from 'react-i18next';
import { LogOut } from 'lucide-react';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@scio3/ui';
import { useAuthStore } from '@/stores/auth-store';
import { fullLogout } from '@/lib/logout';
import { StaffSidebar } from './StaffSidebar';

function roleLabel(role: string, t: (k: string) => string): string {
  switch (role) {
    case 'root':
      return t('Root');
    case 'engineer':
      return t('Engineer');
    case 'support':
      return t('Support');
    case 'readonly':
      return t('Read only');
    default:
      return role;
  }
}

export function StaffShell() {
  const { t } = useTranslation();
  const client = useApolloClient();
  const adminUser = useAuthStore((s) => s.adminUser);
  const firebaseUser = useAuthStore((s) => s.firebaseUser);

  const email = adminUser?.email ?? firebaseUser?.email ?? '';
  const role = adminUser?.role ?? '';

  const handleSignOut = async () => {
    await fullLogout(client);
  };

  return (
    <div className="flex h-screen flex-col bg-background" data-testid="staff-shell">
      {/* Top bar */}
      <header className="flex h-14 shrink-0 items-center justify-between border-b bg-card px-6">
        <div className="flex items-center gap-2">
          <span className="text-base font-semibold text-foreground">{t('OptiSigns Staff')}</span>
        </div>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="cursor-pointer"
              data-testid="user-menu-trigger"
            >
              <span className="text-sm">{email}</span>
              {role ? (
                <span className="ml-2 rounded-sm bg-secondary px-2 py-0.5 text-xs font-medium text-secondary-foreground">
                  {roleLabel(role, t)}
                </span>
              ) : null}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel className="truncate">{email}</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="cursor-pointer"
              onClick={handleSignOut}
              data-testid="sign-out-button"
            >
              <LogOut className="mr-2 h-4 w-4" />
              {t('Sign out')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </header>

      {/* Body: sidebar + content */}
      <div className="flex flex-1 overflow-hidden">
        <StaffSidebar />
        <main className="flex-1 overflow-auto" data-testid="staff-main-content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
